import React, { useState, useMemo } from 'react';
import {
    Button,
    Card,
    Col,
    Divider,
    Input,
    Row,
    Space,
    Tag,
    Typography,
    Table,
    Modal
} from 'antd';
import {
    EyeOutlined,
    SearchOutlined,
    ShopOutlined,
    CalendarOutlined,
    CreditCardOutlined
} from '@ant-design/icons';
import PageTitle from '../../../components/common/PageTitle';
import { salesInvoiceList } from '../../../utility/mockData';

const { Text, Title } = Typography;

const SalesInvoice = () => {
    const config = {
        title: 'Sales Invoices',
        subtitle: 'Retrieve and view official customer-care sales invoices.',
    };
    // --- SALES INVOICE STATES ---
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [isInvoiceModalVisible, setIsInvoiceModalVisible] = useState(false);


    // --- SALES INVOICE RENDER LOGIC ---
    const filteredInvoices = useMemo(() => {
        return salesInvoiceList.filter(invoice => {
            const query = invoiceSearch.toLowerCase();
            return (
                invoice.invoiceNo.toLowerCase().includes(query) ||
                invoice.customerName.toLowerCase().includes(query) ||
                (invoice.imei && invoice.imei.toLowerCase().includes(query))
            );
        });
    }, [invoiceSearch]);



        const handleOpenInvoiceModal = (invoice) => {
            setSelectedInvoice(invoice);
            setIsInvoiceModalVisible(true);
        };

        const invoiceColumns = [
            {
                title: 'Invoice No',
                dataIndex: 'invoiceNo',
                key: 'invoiceNo',
                width: 150,
                render: (text, record) => (
                    <Text strong style={{ color: '#175AA0', cursor: 'pointer' }} onClick={() => handleOpenInvoiceModal(record)}>
                        {text}
                    </Text>
                )
            },
            {
                title: 'Invoice Date',
                dataIndex: 'invoiceDate',
                key: 'invoiceDate',
                width: 150,
                render: (date) => (
                    <Space>
                        <CalendarOutlined style={{ color: '#bfbfbf' }} />
                        <Text>{date}</Text>
                    </Space>
                )
            },
            {
                title: 'Branch Office',
                dataIndex: 'branchName',
                key: 'branchName',
                width: 250,
            },
            {
                title: 'Customer Name',
                dataIndex: 'customerName',
                key: 'customerName',
                width: 180,
                render: (text) => <Text strong>{text}</Text>
            },
            {
                title: 'Total Amount',
                dataIndex: 'amount',
                key: 'amount',
                align: 'right',
                width: 150,
                render: (amount) => <Text strong>₹{amount.toLocaleString('en-IN')}</Text>
            },
            {
                title: 'IMEI',
                dataIndex: 'imei',
                key: 'imei',
                width: 200,
                render: (imei) => <Text code>{imei}</Text>
            },
            {
                title: 'Actions',
                key: 'actions',
                align: 'center',
                width: 150,
                render: (_, record) => (
                    <Space size="middle">
                        <Button
                            type="text"
                            icon={<EyeOutlined style={{ color: '#175AA0' }} />}
                            onClick={() => handleOpenInvoiceModal(record)}
                        >
                            View Receipt
                        </Button>
                    </Space>
                )
            }
        ];

        return (
            <PageTitle title={config.title} subtitle={config.subtitle}>
                <Card style={{ borderRadius: '16px', border: '1px solid #e6edf7' }}>
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={24} md={12}>
                            <Space.Compact style={{ width: '100%' }}>
                                <Input
                                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                    placeholder="Search by customer, invoice no, or IMEI..."
                                    allowClear
                                    size="large"
                                    value={invoiceSearch}
                                    onChange={(e) => setInvoiceSearch(e.target.value)}
                                    style={{ borderRadius: '8px 0 0 8px', height: '46px' }}
                                />
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<SearchOutlined />}
                                    style={{ background: '#175AA0', borderColor: '#175AA0', borderRadius: '0 8px 8px 0', height: '46px' }}
                                >
                                    Search
                                </Button>
                            </Space.Compact>
                        </Col>
                    </Row>

                    <Table
                        columns={invoiceColumns}
                        dataSource={filteredInvoices}
                        rowKey="invoiceNo"
                        bordered
                        pagination={{ pageSize: 5 }}
                        scroll={{ x: 'max-content' }}
                    />
                </Card>

                {/* Receipt View Modal */}
                <Modal
                    visible={isInvoiceModalVisible}
                    onCancel={() => setIsInvoiceModalVisible(false)}
                    footer={[
                        <Button key="close" onClick={() => setIsInvoiceModalVisible(false)}>
                            Close
                        </Button>
                    ]}
                    width={650}
                    bodyStyle={{ padding: '0 10px' }}
                >
                    {selectedInvoice && (
                        <div style={{ padding: '16px 0', fontFamily: 'sans-serif' }}>
                            {/* Brand Header */}
                            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                                <Title level={3} style={{ color: '#175AA0', margin: 0, letterSpacing: '1px' }}>SANGEETHA MOBILES</Title>
                                <Text type="secondary" style={{ fontSize: '12px' }}>India's Premier Mobile Retailer</Text>
                                <div style={{ height: '2px', background: '#fa8c16', margin: '8px 0' }}></div>
                                <Text strong style={{ fontSize: '13px' }}>CUSTOMER CARE DUPLICATE INVOICE COPY</Text>
                            </div>

                            {/* Metadata */}
                            <Row justify="space-between" style={{ marginBottom: '20px' }}>
                                <Col span={11}>
                                    <Space direction="vertical" size={2}>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>INVOICE METADATA</Text>
                                        <Text strong style={{ fontSize: '15px' }}>{selectedInvoice.invoiceNo}</Text>
                                        <Text><CalendarOutlined /> Date: {selectedInvoice.invoiceDate}</Text>
                                        <Text><ShopOutlined /> Branch: {selectedInvoice.branchName}</Text>
                                        <Text><CreditCardOutlined /> Paid via: {selectedInvoice.paymentMode}</Text>
                                    </Space>
                                </Col>
                                <Col span={11} style={{ textAlign: 'right' }}>
                                    <Space direction="vertical" size={2} style={{ alignItems: 'flex-end' }}>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>CUSTOMER INFO</Text>
                                        <Text strong style={{ fontSize: '15px' }}>{selectedInvoice.customerName}</Text>
                                        <Text>{selectedInvoice.customerPhone}</Text>
                                        <Text>{selectedInvoice.customerEmail}</Text>
                                        <Tag color="success" style={{ margin: '4px 0 0 0' }}>{selectedInvoice.status.toUpperCase()}</Tag>
                                    </Space>
                                </Col>
                            </Row>

                            {/* Table */}
                            <Table
                                size="small"
                                rowKey="sku"
                                dataSource={selectedInvoice.items}
                                pagination={false}
                                bordered
                                columns={[
                                    {
                                        title: 'Item SKU / Description',
                                        key: 'description',
                                        render: (_, item) => (
                                            <Space direction="vertical" size={0}>
                                                <Text strong>{item.modelName}</Text>
                                                <Text type="secondary" style={{ fontSize: '11px' }}>{item.sku} ({item.brand} • {item.category})</Text>
                                            </Space>
                                        )
                                    },
                                    {
                                        title: 'Qty',
                                        dataIndex: 'quantity',
                                        key: 'quantity',
                                        align: 'center',
                                    },
                                    {
                                        title: 'Unit Price',
                                        dataIndex: 'price',
                                        key: 'price',
                                        align: 'right',
                                        render: (p) => `₹${p.toLocaleString('en-IN')}`
                                    },
                                    {
                                        title: 'Total',
                                        dataIndex: 'total',
                                        key: 'total',
                                        align: 'right',
                                        render: (t) => `₹${t.toLocaleString('en-IN')}`
                                    }
                                ]}
                            />

                            {/* Total Breakdown */}
                            <Row justify="end" style={{ marginTop: '16px' }}>
                                <Col span={12} style={{ textAlign: 'right', borderTop: '2px solid #f0f0f0', paddingTop: '8px' }}>
                                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                        <Row justify="space-between">
                                            <Col span={12}><Text type="secondary">Subtotal (Excl. Tax):</Text></Col>
                                            <Col span={12}><Text>₹{selectedInvoice.subtotal.toLocaleString('en-IN')}</Text></Col>
                                        </Row>
                                        <Row justify="space-between">
                                            <Col span={12}><Text type="secondary">GST (18% included):</Text></Col>
                                            <Col span={12}><Text>₹{selectedInvoice.taxAmount.toLocaleString('en-IN')}</Text></Col>
                                        </Row>
                                        <Divider style={{ margin: '8px 0' }} />
                                        <Row justify="space-between">
                                            <Col span={12}><Text strong style={{ fontSize: '16px', color: '#175AA0' }}>Grand Total:</Text></Col>
                                            <Col span={12}><Text strong style={{ fontSize: '16px', color: '#175AA0' }}>₹{selectedInvoice.amount.toLocaleString('en-IN')}</Text></Col>
                                        </Row>
                                    </Space>
                                </Col>
                            </Row>

                            <div style={{ marginTop: '28px', background: '#f5f6fa', padding: '10px', borderRadius: '6px', textAlign: 'center' }}>
                                <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                    If you have any support queries regarding this device or invoice, please reach out to customer care at support@sangeethamobiles.com or dial 080-49174917.
                                </Text>
                            </div>
                        </div>
                    )}
                </Modal>
            </PageTitle>
        );
};

export default SalesInvoice;
